/**
 * Provider-neutral AI policy.
 *
 * Life OS treats AI as an online-enhanced capability with graceful degradation:
 * an on-device local assistant always works; a hosted provider is used only
 * when it is configured, the network is up, policy allows remote use, AND the
 * content's privacy class is eligible to leave the device. Sensitive content is
 * never sent to a hosted provider — it falls back to the local assistant.
 *
 * Everything here is PURE. The data layer holds the actual provider adapters.
 */

import { type PrivacyClass, aiEligibleByDefault } from './privacy.js';

export type AiCapability = 'chat' | 'summarize' | 'plan' | 'tidy';

export interface AiMessage {
  readonly role: 'system' | 'user' | 'assistant';
  readonly content: string;
}

/** Why the local assistant was used instead of a hosted provider. */
export type LocalReason = 'offline' | 'not_configured' | 'policy_remote_disabled' | 'privacy';

export type AiRoute =
  | { readonly kind: 'remote' }
  | { readonly kind: 'local'; readonly reason: LocalReason };

export interface AiContext {
  /** A hosted provider key is present. */
  readonly configured: boolean;
  readonly online: boolean;
  /** Owner policy switch (config `LIFEOS_AI_REMOTE_ENABLED`). */
  readonly remoteAllowedByPolicy: boolean;
}

/**
 * Decide whether a request goes to a hosted provider or the local assistant.
 * Sensitive content (not ai-eligible) is ALWAYS kept local. Pure.
 */
export function routeAiRequest(privacyClass: PrivacyClass, ctx: AiContext): AiRoute {
  if (!aiEligibleByDefault(privacyClass)) return { kind: 'local', reason: 'privacy' };
  if (!ctx.configured) return { kind: 'local', reason: 'not_configured' };
  if (!ctx.online) return { kind: 'local', reason: 'offline' };
  if (!ctx.remoteAllowedByPolicy) return { kind: 'local', reason: 'policy_remote_disabled' };
  return { kind: 'remote' };
}

/**
 * Redact a message's content if its privacy class is not eligible for hosted
 * AI. Defense in depth: even though sensitive requests never route remote, any
 * payload that *would* leave the device is scrubbed. Pure.
 */
export function redactForAi(content: string, privacyClass: PrivacyClass): string {
  return aiEligibleByDefault(privacyClass) ? content : '(redacted: private content withheld from AI)';
}

export interface AiResult {
  readonly text: string;
  readonly usedProvider: 'remote' | 'local';
  readonly reason: LocalReason | null;
}
